import { createSlice } from '@reduxjs/toolkit';
import {
  notificationType,
  pollingType,
  pollingOptions,
  levelDetail,
  jobType,
} from 'helpers';
import {
  serviceGetCredentials,
  serviceGetPlaybooks,
  serviceGetProjectsUsers,
  serviceGetInventories,
  serviceGetTemplate,
  servicePostTemplate,
  servicePostWorkflow,
  serviceDeleteTemplate,
  servicePutTemplate,
  serviceGetTemplateInteraction,
  serviceGetTemplateByProject
} from 'utils';

const initialState = {
  fieldDataConst: {
    notificationType,
    pollingType,
    pollingOptions,
    levelDetail,
    jobType,
  },
  fieldData: {
    userProjects: [],
    playbooks: [],
    credentials: [],
    inventories: [],
  },
  data: null,
  job: null,
  isLoaded: {
    fieldData: false,
    post: false,
    put: false,
    delete: false,
    get: false,
    getRun: false,
    postRun: false
  },
  success: {
    fieldData: false,
    post: false,
    put: false,
    delete: false,
    get: false,
    getRun: false,
    postRun: false
  },
  error: {
    fieldData: false,
    post: false,
    put: false,
    delete: false,
    get: false,
    getRun: false,
    postRun: false
  },
};

const templateSlice = createSlice({
  name: 'template',
  initialState,
  reducers: {
    getFieldDataSuccess(state, action) {
      const data = action.payload;
      
      const playbooks = [
        ...data[1].list_files.map((i) => ({ value: i, label: i })),
      ];
      const userProjects = [...data[2]];

      const oldVersionApi = JSON.parse(process.env.REACT_APP_OLD_VERSION_API || false) || false;

      let inventories = [];

      let credentials = [];

      if (oldVersionApi) {
        inventories = [ ...data[3].list_files.map((i) => ({ value: i, label: i })) ];
        
        credentials = [
          { value: 0, label: 'common.rules.empty' },
          ...data[0].map((i) => ({ value: i.credentials_id, label: i.name })),
        ];

      } else {
        inventories = [ ...data[3].map((i) => ({ value: i.name, label: i.name })) ];

        credentials = [
          ...data[0].map((i) => ({ value: i.credential_id, label: i.name })),
        ];
      }

      return {
        ...state,
        isLoaded: { ...state.isLoaded, fieldData: false },
        success: { ...state.success, fieldData: true },
        fieldData: {
          credentials,
          playbooks,
          userProjects,
          inventories,
        },
      };
    },

    getUserProjectSuccess(state, action) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, fieldData: false },
        success: { ...state.success, fieldData: true },
        fieldData: { ...state.fieldData, userProjects: action.payload },
      };
    },

    getFieldDataRequest(state, action) {
      return { ...state, isLoaded: { ...state.isLoaded, fieldData: true } };
    },

    getFieldDataFailure(state, action) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, fieldData: false },
        error: { ...state.error, fieldData: action.payload.message },
      };
    },

    dataRequest(state, action) {
      return { ...state, isLoaded: { ...state.isLoaded, [action.payload]: true } };
    },

    dataSuccess(state, action) {
      const dataType = action.payload.dataType === 'job' ? 'job' : 'data';

      return {
        ...state,
        isLoaded: { ...state.isLoaded, [action.payload.fetchType]: false },
        success: { ...state.success, [action.payload.fetchType]: true },
        [dataType]: action.payload.data || null,
      };
    },

    dataFailure(state, action) {
      return {
        ...state,
        isLoaded: { ...state.isLoaded, [action.payload.fetchType]: false },
        error: { ...state.error, [action.payload.fetchType]: action.payload.data?.message },
      };
    },

    cleanUpTemplate(state) {
      return initialState;
    },

    cleanUpDataFieldTemplate(state) {
      return {
        ...state,
        fieldData: {
          userProjects: [],
          playbooks: [],
          credentials: [],
          inventories: [],
        },
      };
    },

    cleanUpMessageTemplate(state) {
      return {
        ...state,
        dataType: null,
        isLoaded: initialState.isLoaded,
        success: initialState.success,
        error: initialState.error,
      };
    },
  },
});

const {
  getFieldDataSuccess,
  getUserProjectSuccess,
  getFieldDataRequest,
  getFieldDataFailure,
  dataRequest,
  dataSuccess,
  dataFailure,
  cleanUpMessageTemplate,
  cleanUpDataFieldTemplate,
  cleanUpTemplate,
} = templateSlice.actions;

const fetchGetFieldData = (args) => {
  return (dispatch) => {
    dispatch(cleanUpDataFieldTemplate());
    dispatch(getFieldDataRequest());
    Promise.all([
      serviceGetCredentials(args),
      serviceGetPlaybooks(args),
      serviceGetProjectsUsers(args),
      serviceGetInventories(args),
    ])
      .then((response) => {
        const resultResponse = response.map((res) => res.data);
        dispatch(getFieldDataSuccess(resultResponse));
      })
      .catch((err) => dispatch(getFieldDataFailure(err)));
  };
};

const fetchGetUserProject = (args) => {
  return (dispatch) => {
    dispatch(cleanUpDataFieldTemplate());
    dispatch(getFieldDataRequest());
    serviceGetProjectsUsers(args)
      .then((response) => dispatch(getUserProjectSuccess(response.data)))
      .catch((err) => dispatch(getFieldDataFailure(err)));
  };
};

const fetchGetTemplate = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('get'));
    serviceGetTemplate(args)
      .then((response) => dispatch(dataSuccess({data: response.data, fetchType: 'get'})))
      .catch((err) => dispatch(dataFailure({data: err, fetchType: 'get'})));
  };
};

const fetchPostTemplate = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('post'));
    servicePostTemplate(args)
      .then((response) => dispatch(dataSuccess({data: response.data, fetchType: 'post'})))
      .catch((err) => dispatch(dataFailure({data: err, fetchType: 'post'})));
  };
};

const fetchPostWorkflow = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('post'));
    servicePostWorkflow(args)
      .then((response) => dispatch(dataSuccess({data: response.data, fetchType: 'post'})))
      .catch((err) => dispatch(dataFailure({data: err, fetchType: 'post'})));
  };
};

const fetchPutTemplate = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('put'));
    servicePutTemplate(args)
      .then((response) => dispatch(dataSuccess({data: response.data, fetchType: 'put'})))
      .catch((err) => dispatch(dataFailure({data: err, fetchType: 'put'})));
  };
};

const fetchDeleteTemplate = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('delete'));
    serviceDeleteTemplate(args)
      .then((response) => dispatch(dataSuccess({data: response.data, fetchType: 'delete'})))
      .catch((err) => dispatch(dataFailure({data: err, fetchType: 'delete'})));
  };
};

const fetchGetTemplateInteraction = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('getRun'));
    serviceGetTemplateInteraction(args)
      .then((response) => dispatch(dataSuccess({ data: response.data, fetchType: 'getRun', dataType: 'job' })))
      .catch((err) => dispatch(dataFailure({ data: err, fetchType: 'getRun' })));
  };
};

const fetchGetTemplateByProject = (args) => {
  return (dispatch) => {
    dispatch(dataRequest('getTemplateProject'));
    serviceGetTemplateByProject(args)
      .then((response) => dispatch(dataSuccess({ data: response.data, fetchType: 'getTemplateProject' })))
      .catch((err) => dispatch(dataFailure({ data: err, fetchType: 'getTemplateProject' })));
  };
};

export {
  fetchGetFieldData,
  fetchGetUserProject,
  fetchPostWorkflow,
  fetchPostTemplate,
  fetchGetTemplate,
  fetchDeleteTemplate,
  fetchPutTemplate,
  fetchGetTemplateInteraction,
  fetchGetTemplateByProject,
  cleanUpMessageTemplate,
  cleanUpDataFieldTemplate,
  cleanUpTemplate,
};

export default templateSlice.reducer;
